import {
  MONITOR_ENVIRONMENT_HINTS,
  MONITOR_ENVIRONMENT_LABELS,
  REMOTE_ORIGIN_LABELS,
  REMOTE_OUTCOME_LABELS,
  REMOTE_STATUS_LABELS,
  SOURCE_HEALTH_LABELS,
  type MonitorEnvironment,
  type RemoteCallOrigin,
  type RemoteCallOutcome,
  type RemoteCallStatus,
  type SourceHealthState,
} from "@shared/types";
import { Badge } from "../ui/badge";

/**
 * L'environnement est l'information la plus importante de l'écran : il se lit
 * avant tout le reste. Production = pastille pleine, préproduction = pastille
 * ambrée hachurée, toujours avec son sens en clair (clients réels / tests).
 */
export function EnvBadge({ environment, compact = false }: { environment: MonitorEnvironment; compact?: boolean }) {
  return (
    <span className={`env-badge env-badge--${environment}`}>
      <span className="env-badge__dot" aria-hidden="true" />
      <span className="env-badge__name">{MONITOR_ENVIRONMENT_LABELS[environment]}</span>
      {compact ? null : <span className="env-badge__hint">· {MONITOR_ENVIRONMENT_HINTS[environment]}</span>}
    </span>
  );
}

/** Téléphone (vrai appel) ou simulation (relais textuel, scénario) : jamais confondus. */
export function OriginBadge({ origin }: { origin: RemoteCallOrigin }) {
  return (
    <Badge variant={origin === "telephone" ? "primary" : "neutral"}>{REMOTE_ORIGIN_LABELS[origin]}</Badge>
  );
}

export function InternalTestBadge() {
  return <Badge variant="warning">Test interne</Badge>;
}

const OUTCOME_VARIANT: Record<RemoteCallOutcome, "neutral" | "primary" | "success" | "warning" | "danger"> = {
  rdv_pris: "success",
  message: "warning",
  rappel: "warning",
  renseignement: "primary",
  transfert: "primary",
  aucun: "neutral",
};

/** Un appel se lit d'abord par ce qu'il a produit ; le statut ne s'affiche que s'il dit autre chose. */
export function ResultBadge({ status, outcome }: { status: RemoteCallStatus; outcome: RemoteCallOutcome }) {
  if (status === "en_cours") return <Badge variant="live">{REMOTE_STATUS_LABELS.en_cours}</Badge>;
  if (status === "manque") return <Badge variant="danger">{REMOTE_STATUS_LABELS.manque}</Badge>;
  return <Badge variant={OUTCOME_VARIANT[outcome]}>{REMOTE_OUTCOME_LABELS[outcome]}</Badge>;
}

export function HealthBadge({ state }: { state: SourceHealthState }) {
  const variant = state === "ok" ? "success" : state === "unreachable" || state === "error" ? "danger" : "warning";
  return <Badge variant={variant}>{SOURCE_HEALTH_LABELS[state]}</Badge>;
}
